<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  'recordError',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const recordedAudioUrl = ref(null);
const recordedDurationMs = ref(0);
const playbackCurrentTimeMs = ref(0);
const playbackProgressPercentage = ref(0);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const getAudioExtension = mimeType => {
  if (!mimeType) return 'ogg';
  const type = mimeType.toLowerCase();
  if (type.includes('ogg')) return 'ogg';
  if (type.includes('mp4') || type.includes('m4a')) return 'mp4';
  if (type.includes('mp3') || type.includes('mpeg')) return 'mp3';
  if (type.includes('wav')) return 'wav';
  if (type.includes('webm')) return 'webm';
  return 'ogg';
};

const recordedFile = ref(null);

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#10b981',
    progressColor: '#34d399',
    height: 32,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', blob => {
    try {
      if (!blob || blob.size === 0) {
        isRecording.value = false;
        hasRecording.value = false;
        return;
      }
      const rawType = blob.type || 'audio/ogg;codecs=opus';
      const cleanType = rawType.split(';')[0].trim() || 'audio/ogg';
      const ext = getAudioExtension(cleanType);
      const fileName = `${getUuid()}.${ext}`;
      const file = new File([blob], fileName, {
        type: cleanType,
      });
      file.isVoiceMessage = true;
      recordedFile.value = file;

      if (recordedAudioUrl.value) URL.revokeObjectURL(recordedAudioUrl.value);
      recordedAudioUrl.value = URL.createObjectURL(blob);

      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
      hasRecording.value = true;
      isRecording.value = false;
      playbackProgressPercentage.value = 0;
      playbackCurrentTimeMs.value = 0;
      emit('recorderProgressChanged', formatTimeProgress(recordedDurationMs.value));
    } catch (error) {
      console.error('Audio recorder finish error:', error);
      isRecording.value = false;
      hasRecording.value = false;
      emit('recordError', { error });
    }
  });

  record.value.on('record-progress', time => {
    recordedDurationMs.value = time;
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value && record.value) {
    try {
      record.value.stopRecording();
    } catch (err) {
      console.warn('stopRecording error:', err);
    }
    isRecording.value = false;
  }
};

const stopAndGetFile = () => {
  return new Promise(resolve => {
    if (recordedFile.value) {
      resolve(recordedFile.value);
      return;
    }

    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve(recordedFile.value || null);
      }
    }, 1500);

    const onRecordEndOnce = blob => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeout);
      try {
        if (!blob || blob.size === 0) {
          resolve(null);
          return;
        }
        const rawType = blob.type || 'audio/ogg;codecs=opus';
        const cleanType = rawType.split(';')[0].trim() || 'audio/ogg';
        const ext = getAudioExtension(cleanType);
        const fileName = `${getUuid()}.${ext}`;
        const file = new File([blob], fileName, { type: cleanType });
        file.isVoiceMessage = true;
        recordedFile.value = file;
        resolve(file);
      } catch (err) {
        console.error('Error generating recorded audio file:', err);
        resolve(null);
      }
    };

    if (record.value) {
      record.value.once('record-end', onRecordEndOnce);
      stopRecording();
    } else {
      resolve(null);
    }
  });
};

const startRecording = () => {
  if (record.value) {
    try {
      record.value.startRecording();
      isRecording.value = true;
      recordedDurationMs.value = 0;
      playbackCurrentTimeMs.value = 0;
      playbackProgressPercentage.value = 0;
    } catch (err) {
      console.warn('startRecording error:', err);
      isRecording.value = false;
    }
  }
};

const previewAudio = ref(null);

const onPreviewTimeUpdate = () => {
  if (!previewAudio.value) return;
  const currentSec = previewAudio.value.currentTime || 0;
  const durSec = previewAudio.value.duration || (recordedDurationMs.value / 1000) || 0;
  playbackCurrentTimeMs.value = currentSec * 1000;
  if (durSec > 0) {
    playbackProgressPercentage.value = Math.min(100, Math.max(0, (currentSec / durSec) * 100));
  }
  emit('recorderProgressChanged', formatTimeProgress(playbackCurrentTimeMs.value));
};

const onPreviewEnded = () => {
  isPlaying.value = false;
  playbackProgressPercentage.value = 0;
  playbackCurrentTimeMs.value = 0;
  emit('pause');
  emit('recorderProgressChanged', formatTimeProgress(recordedDurationMs.value));
};

const playPause = () => {
  if (isRecording.value) {
    stopRecording();
    return;
  }
  if (previewAudio.value && recordedAudioUrl.value) {
    if (isPlaying.value) {
      previewAudio.value.pause();
      isPlaying.value = false;
      emit('pause');
      emit('recorderProgressChanged', formatTimeProgress(recordedDurationMs.value));
    } else {
      previewAudio.value.play();
      isPlaying.value = true;
      emit('play');
    }
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (isRecording.value && record.value) {
    try {
      record.value.stopRecording();
    } catch {
      /* ignore */
    }
  }
  if (previewAudio.value) {
    try {
      previewAudio.value.pause();
    } catch {
      /* ignore */
    }
  }
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = null;
  }
  if (wavesurfer.value) {
    try {
      wavesurfer.value.destroy();
    } catch {
      /* ignore */
    }
  }
});

defineExpose({ playPause, stopRecording, record, stopAndGetFile, getRecordedFile: () => recordedFile.value });
</script>

<template>
  <div class="relative w-full p-1 flex flex-col gap-1">
    <div ref="waveformContainer" class="w-full" />
    <div
      v-if="hasRecording"
      class="w-full px-2 py-0.5 flex items-center gap-3 animate-fade-in"
    >
      <div class="flex-1 bg-slate-200 dark:bg-slate-700/60 h-1.5 rounded-full overflow-hidden">
        <div
          class="bg-woot-500 h-full rounded-full transition-all duration-100 ease-linear"
          :style="{ width: `${playbackProgressPercentage}%` }"
        />
      </div>
      <span class="text-xs text-slate-500 dark:text-slate-400 font-mono font-medium select-none">
        {{ formatTimeProgress(playbackCurrentTimeMs) }} / {{ formatTimeProgress(recordedDurationMs) }}
      </span>
    </div>
  </div>
  <audio
    ref="previewAudio"
    class="hidden"
    :src="recordedAudioUrl"
    @timeupdate="onPreviewTimeUpdate"
    @ended="onPreviewEnded"
    @pause="() => { isPlaying = false; emit('pause'); }"
  />
</template>
